import { IconButton } from '@lastbrand/web-ui/icon-button'
import { SaveIcon, SaveSolidIcon } from '@lastbrand/web-ui/icons'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useState } from 'react'

import { useProductWishList } from '@src/hooks/useProductWishlist'
import useDeviceInfo from '@src/quince-site-common/src/hooks/useDeviceInfo'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

import type { AddToWishlistButtonProps } from './add-to-wishlist-button.types'

const ACTIVE_HEART_CSS = css.raw({
  color: 'black',
  _hover: { color: 'gray', bg: 'transparent' },
  _active: { color: 'mediumgray', bg: 'transparent' },
})

const INACTIVE_HEART_CSS = css.raw({
  // Outline heart carries a 30%-white interior fill — in the design but absent
  // from the Figma handoff spec (`custom/white-trans-30`).
  color: 'mediumgray',
  '& svg': { fill: 'white-trans-30' },
  _hover: { color: 'gray', bg: 'transparent' },
  _active: { color: 'black', bg: 'transparent' },
})

export function AddToWishlistButton({
  spid,
  selectedColor,
  wishlistTrackConfig,
  wishlistEnabled = true,
  hideWishlistIcon = false,
  productName,
  wishlistAriaLabel,
  wishlistAriaLabelSelected,
  tileVariant = 'default',
  wishlistButtonCss,
  wishlistDisabled,
}: AddToWishlistButtonProps) {
  const { t } = useTranslation('common')
  const { isNativeView } = useDeviceInfo()
  const isLoggedIn = isBrowser() ? AuthUtil.isLoggedIn() : false
  const { isItemInWishList, addItemToWishList, removeItemFromWishList } = useProductWishList()
  const [isWishlisted, setIsWishlisted] = useState(() => isItemInWishList(spid, selectedColor))

  const resolvedAriaLabel = wishlistAriaLabel ?? t('pc_add_wishlist_aria', { productName: productName ?? '' })
  const resolvedAriaLabelSelected =
    wishlistAriaLabelSelected ?? t('pc_remove_wishlist_aria', { productName: productName ?? '' })

  const isMini = tileVariant === 'mini'
  const isCompact = tileVariant === 'compact'
  const isNarrow = isCompact || isMini

  const showWishlist = wishlistEnabled && !(isNativeView && !isLoggedIn) && !hideWishlistIcon

  useEffect(() => {
    setIsWishlisted(isItemInWishList(spid, selectedColor))
  }, [isItemInWishList, spid, selectedColor])

  const onWishlistToggle = useCallback(() => {
    if (isWishlisted) {
      setIsWishlisted(false)
      removeItemFromWishList(spid, selectedColor, wishlistTrackConfig ?? {}).catch((e: unknown) => {
        console.error(e)
      })
    } else {
      setIsWishlisted(true)
      addItemToWishList(spid, selectedColor, wishlistTrackConfig ?? {}).catch((e: unknown) => {
        console.error(e)
      })
    }
  }, [addItemToWishList, isWishlisted, removeItemFromWishList, selectedColor, spid, wishlistTrackConfig])

  if (!showWishlist) return null

  return (
    <div
      className={css({
        position: 'absolute',
        top: { base: 'space-8', lg: isNarrow ? 'space-8' : 'space-16' },
        right: { base: 'space-8', lg: isNarrow ? 'space-8' : 'space-16' },
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      })}
    >
      <IconButton
        type="button"
        variant="ghost"
        aria-label={isWishlisted ? resolvedAriaLabelSelected : resolvedAriaLabel}
        aria-pressed={isWishlisted}
        onClick={onWishlistToggle}
        disabled={wishlistDisabled}
        css={{
          flexShrink: 0,
          bg: 'transparent',
          ...(isWishlisted ? ACTIVE_HEART_CSS : INACTIVE_HEART_CSS),
          ...wishlistButtonCss,
        }}
      >
        {isWishlisted ? <SaveSolidIcon width={20} height={20} /> : <SaveIcon width={20} height={20} />}
      </IconButton>
    </div>
  )
}
